import React from 'react';
import {Link} from 'react-router-dom';
import i18next from 'i18next';
import {isDesktop} from 'react-device-detect';
import Picture from './Picture';
import logo from '../assets/Images/logo150.png';
import dataCharacter from '../assets/dataCharacter.json';
import {getCampaignHue} from '../utils/campaignVisual';
import '../styles/campaignHome.css';

const CHARACTERISTICS_ORDER = dataCharacter.characteristics.map(c => c.label);
const orderOf = (label) => {
  const index = CHARACTERISTICS_ORDER.indexOf(label);
  return index === -1 ? CHARACTERISTICS_ORDER.length : index;
};

const lastUpdate = (char) => (char.lastUpdateAt && char.lastUpdateAt.seconds) || 0;

const HpBar = ({current, max}) => {
  const ratio = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
  const level = ratio > 0.5 ? 'high' : ratio > 0.25 ? 'mid' : 'low';
  return (
    <div className='homeHp'>
      <div className='homeHpTrack'>
        <div className={`homeHpFill ${level}`} style={{width: `${ratio * 100}%`}} />
      </div>
      <span className='homeHpValue'>{current} / {max} HP</span>
    </div>
  );
};

/**
 * Accueil d'une campagne Aria : le joueur voit son personnage en grand,
 * le MJ voit son espace puis tous les personnages de la table.
 */
const AriaCampaignHome = ({campaign, characters, loading, userUid, baseUrl, onSelectCharacter, onCreateCharacter}) => {
  if (loading) {
    return (
      <div className='campaignHome' aria-busy='true'>
        <div className='campaignHomeHero skeleton' />
        <div className='homeSkeletonCard skeleton' />
        <div className='homeTableGrid'>
          <div className='homeSkeletonRow skeleton' />
          <div className='homeSkeletonRow skeleton' />
          <div className='homeSkeletonRow skeleton' />
        </div>
      </div>
    );
  }

  const isDm = userUid === campaign.idUserDm;
  const sorted = [...characters].sort((a, b) => lastUpdate(b) - lastUpdate(a));
  const [mainCharacter, ...otherCharacters] = sorted;
  const createdAt = campaign.createdAt && campaign.createdAt.seconds
    ? new Date(campaign.createdAt.seconds * 1000).toLocaleDateString(i18next.language, {month: 'long', year: 'numeric'})
    : null;

  return (
    <div className='campaignHome'>
      <header
        className={`campaignHomeHero${campaign.coverPicture ? ' hasImage' : ''}`}
        style={{
          '--campaign-hue': getCampaignHue(campaign.uid),
          backgroundImage: campaign.coverPicture ? `linear-gradient(to top, rgba(0,0,0,0.75), rgba(0,0,0,0.15) 75%), url(${campaign.coverPicture})` : undefined,
        }}
      >
        <div className='campaignHomeHeroContent'>
          <Link className='campaignHomeBack' to='/campaigns'>← {i18next.t('my campaign')}</Link>
          <h1 className='campaignHomeName'>{campaign.name}</h1>
          <div className='campaignHomeMeta'>
            {campaign.createdBy && <span>{i18next.t('dm')} : <strong>{campaign.createdBy}</strong></span>}
            {createdAt && <span>{createdAt}</span>}
            {isDm && campaign.invitationCode && (
              <span className='campaignHomeCode'>{i18next.t('invitation code')} <strong>{campaign.invitationCode}</strong></span>
            )}
          </div>
        </div>
        {isDesktop && (
          <Link className='campaignHomeStats' to={`${baseUrl}/statistics`}>
            {i18next.t('stats.title')}
          </Link>
        )}
      </header>

      {isDm ? (
        <>
          <Link className='homeDmCard' to={`${baseUrl}/dm`} onClick={() => onSelectCharacter(null)}>
            <Picture character={{picture: logo}} />
            <div className='homeDmCardText'>
              <span className='homeDmCardTitle'>{i18next.t('campaignHome.dmSpace')}</span>
              <span className='homeDmCardDesc'>{i18next.t('campaignHome.dmSpaceDesc')}</span>
            </div>
            <span className='homeCardArrow' aria-hidden='true'>→</span>
          </Link>

          <div className='homeSectionHeader'>
            <h2 className='homeSectionTitle'>
              {i18next.t('campaignHome.tableCharacters')}
              <span className='homeSectionCount'>{characters.length}</span>
            </h2>
            <button className='homeTextButton' onClick={onCreateCharacter}>+ {i18next.t('campaignHome.newCharacter')}</button>
          </div>
          {characters.length === 0 ? (
            <p className='homeEmptyText'>{i18next.t('campaignHome.noPlayerYet')}</p>
          ) : (
            <div className='homeTableGrid'>
              {sorted.map(char => (
                <Link key={char.uid} className='homeTableCard' to={`${baseUrl}/${char.uid}`} onClick={() => onSelectCharacter(char)}>
                  <Picture character={char} frame='' />
                  <div className='homeTableCardText'>
                    <span className='homeTableCardName'>{char.name}</span>
                    {char.idUser === userUid && <span className='homeTableCardTag'>{i18next.t('campaignHome.yours')}</span>}
                    <HpBar current={char.currentHp} max={char.maxHp} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      ) : mainCharacter ? (
        <>
          <Link className='homeHeroCard' to={`${baseUrl}/${mainCharacter.uid}`} onClick={() => onSelectCharacter(mainCharacter)}>
            <div className='homeHeroPortrait'>
              <Picture character={mainCharacter} />
            </div>
            <div className='homeHeroBody'>
              <span className='homeHeroLabel'>{i18next.t('campaignHome.yourCharacter')}</span>
              <span className='homeHeroName'>{mainCharacter.name}</span>
              <HpBar current={mainCharacter.currentHp} max={mainCharacter.maxHp} />
              {mainCharacter.characteristics && mainCharacter.characteristics.length > 0 && (
                <ul className='homeHeroStats'>
                  {[...mainCharacter.characteristics].sort((a, b) => orderOf(a.label) - orderOf(b.label)).map(c => (
                    <li key={c.label}>
                      <span className='homeHeroStatValue'>{c.value ?? '–'}</span>
                      <span className='homeHeroStatLabel'>{i18next.t(`characteristics.${c.label}`)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <span className='homeHeroCta'>{i18next.t('campaignHome.openSheet')} →</span>
            </div>
          </Link>

          {otherCharacters.length > 0 && (
            <>
              <h2 className='homeSectionTitle'>{i18next.t('campaignHome.otherCharacters')}</h2>
              <div className='homeTableGrid'>
                {otherCharacters.map(char => (
                  <Link key={char.uid} className='homeTableCard' to={`${baseUrl}/${char.uid}`} onClick={() => onSelectCharacter(char)}>
                    <Picture character={char} frame='' />
                    <div className='homeTableCardText'>
                      <span className='homeTableCardName'>{char.name}</span>
                      <HpBar current={char.currentHp} max={char.maxHp} />
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          <button className='homeTextButton homeAnotherCharacter' onClick={onCreateCharacter}>
            + {i18next.t('campaignHome.anotherCharacter')}
          </button>
        </>
      ) : (
        <div className='homeEmptyCard'>
          <span className='homeEmptyIcon' aria-hidden='true'>🎲</span>
          <h2>{i18next.t('campaignHome.noCharacterTitle')}</h2>
          <p>{i18next.t('campaignHome.noCharacterText')}</p>
          <button className='homeEmptyButton' onClick={onCreateCharacter}>{i18next.t('campaignHome.createCharacter')}</button>
        </div>
      )}
    </div>
  );
};

export default AriaCampaignHome;
